//Libs
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";

//Local
import BaseModal from "../BaseModal";
import BaseInputText from "../BaseInputText";
import BaseInputNumber from "../BaseInputNumber";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import StyledMessage from "../StyledMessage";
import { ExistingPurchaseOrder } from "../../models/purchase-order";
import { ExistingPurchaseOrderReceipt } from "../../models/purchase-order-receipt";
import { DollarCurrency } from "../../currency";
import BaseInputTextArea from "../BaseInputTextArea";

const ReceiptFormSchema = z.object({
  invoiceNumber: z.string().min(0).max(200),
  receivedDate: z.date(),
  receivedBy: z.string().min(0).max(200),
  receivingNotes: z.string().max(8000).optional(),
  lineItems: z.array(
    z.object({
      purchaseOrderItemID: z.string(),
      title: z.string(),
      description: z.string().nullable(),
      quantityOrdered: z.number(),
      quantityReceived: z
        .number()
        .min(0, "Received quantity cannot be negative"),
      quantityRejected: z
        .number()
        .min(0, "Rejected quantity cannot be negative"),
      rejectionReason: z.string().max(4000).optional(),
      unitPrice: z.number(),
      priceBookItemID: z.string().nullable(),
    }),
  ),
});

export type ReceiptFormValues = z.infer<typeof ReceiptFormSchema>;

interface Props {
  isDialogOpen: boolean;
  purchaseOrder: ExistingPurchaseOrder;
  receipts: ExistingPurchaseOrderReceipt[];
  closeDialog: () => void;
  handleSave: (formValues: ReceiptFormValues) => Promise<void>;
  loading?: boolean;
  initialValues?: Partial<ReceiptFormValues>;
  isEditing?: boolean;
  editingReceiptId?: string;
  title: string;
  submitButtonText: string;
  busyText: string;
}

export default function ReceiptFormDialog(props: Props) {
  // Helper functions for calculating quantities
  const getItemReceivedQuantity = (itemId: string) => {
    return props.receipts
      .filter(
        (receipt) =>
          // Exclude the current receipt being edited to avoid double counting
          !props.isEditing || receipt.id !== props.editingReceiptId,
      )
      .reduce((total, receipt) => {
        const receiptItem = receipt.lineItems.find(
          (item) => item.purchaseOrderItemID === itemId,
        );
        return total + (receiptItem?.quantityReceived || 0);
      }, 0);
  };

  const calculateRemainingQuantity = (item: any) => {
    const alreadyReceived = getItemReceivedQuantity(item.purchaseOrderItemID);
    return Math.max(0, item.quantityOrdered - alreadyReceived);
  };

  // Helper function to check if receiving more than ordered
  const isReceivingMoreThanOrdered = (item: any, receivingQuantity: number) => {
    const alreadyReceived = getItemReceivedQuantity(item.purchaseOrderItemID);
    const totalReceiving = alreadyReceived + receivingQuantity;
    return totalReceiving > item.quantityOrdered;
  };

  // Helper function to get the excess quantity being received
  const getExcessQuantity = (item: any, receivingQuantity: number) => {
    const alreadyReceived = getItemReceivedQuantity(item.purchaseOrderItemID);
    const totalReceiving = alreadyReceived + receivingQuantity;
    const excess = totalReceiving - item.quantityOrdered;
    return Math.max(0, excess);
  };

  const defaultValues = useMemo(() => {
    const baseValues = {
      invoiceNumber: "",
      receivedDate: new Date(),
      receivedBy: "",
      receivingNotes: "",
      lineItems: props.purchaseOrder.lineItems.map((item) => ({
        purchaseOrderItemID: item.id,
        title: item.title,
        description: item.description,
        quantityOrdered: item.quantity,
        quantityReceived: 0,
        quantityRejected: 0,
        rejectionReason: "",
        unitPrice: item.unitPrice,
        priceBookItemID: item.priceBookItemID,
      })),
    };

    // Merge with initial values if provided
    if (props.initialValues) {
      return {
        ...baseValues,
        ...props.initialValues,
        lineItems: props.initialValues.lineItems || baseValues.lineItems,
      };
    }

    return baseValues;
  }, [props.purchaseOrder, props.initialValues]);

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
    watch,
    setValue,
  } = useForm<ReceiptFormValues>({
    defaultValues,
    resolver: zodResolver(ReceiptFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (props.isDialogOpen) {
      reset(defaultValues);
    }
  }, [props.isDialogOpen, defaultValues, reset]);

  const watchedLineItems = watch("lineItems");

  // Check if any items are being over-received (moved here to access watchedLineItems)
  const hasOverReceivedItems = () => {
    return watchedLineItems.some((item) =>
      isReceivingMoreThanOrdered(item, item.quantityReceived || 0),
    );
  };

  const hasRemainingItems = () => {
    return props.purchaseOrder.lineItems.some((item) => {
      const alreadyReceived = getItemReceivedQuantity(item.id);
      return alreadyReceived < item.quantity;
    });
  };

  // Check if there are any validation errors
  const hasValidationErrors = () => {
    return Object.keys(errors).length > 0;
  };

  const receiptHeader = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl font-semibold">
        {props.title}
      </h1>
      <button type="button" onClick={() => props.closeDialog()}>
        <XMarkIcon aria-label="close receipt form" className="h-6 text-white" />
      </button>
    </div>
  );

  const onClickSaveButton: SubmitHandler<ReceiptFormValues> = async (
    formValues,
  ) => {
    await props.handleSave(formValues);
    props.closeDialog();
  };

  const calculateTotalQuantityReceived = () => {
    return watchedLineItems.reduce(
      (total, item) => total + item.quantityReceived,
      0,
    );
  };

  const handleReceiveAllRemainingItems = () => {
    const updatedLineItems = watchedLineItems.map((item) => {
      const remainingQty = calculateRemainingQuantity(item);
      return {
        ...item,
        quantityReceived: remainingQty,
        quantityRejected: 0,
        rejectionReason: "",
      };
    });
    setValue("lineItems", updatedLineItems);
  };

  // Fix for leading zero issue: ensure value is properly formatted
  const handleNumberInputChange = (field: any, value: string) => {
    const numValue = value === "" ? 0 : Number(value);
    field.onChange(numValue);
  };

  // Get button text based on whether there are remaining items
  const getReceiveAllButtonText = () => {
    return hasRemainingItems()
      ? "Receive All Remaining Items"
      : "Receive All Items";
  };

  return (
    <BaseModal
      closeModal={props.closeDialog}
      open={props.isDialogOpen}
      title={receiptHeader}
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form
        className="flex flex-col space-y-6 p-8 text-lg"
        autoComplete="off"
        onSubmit={handleSubmit(onClickSaveButton)}
      >
        {/* Receipt Information */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <Controller
              name="invoiceNumber"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text="Invoice Number"
                  inputName="invoiceNumber"
                  required={false}
                  {...field}
                />
              )}
            />
            {errors.invoiceNumber?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.invoiceNumber.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          <div>
            <Controller
              name="receivedDate"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text="Received Date"
                  inputName="receivedDate"
                  type="date"
                  value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = parseISO(e.target.value + "T00:00:00");
                    field.onChange(date);
                  }}
                />
              )}
            />
            {errors.receivedDate?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.receivedDate.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          <div>
            <Controller
              name="receivedBy"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text="Received By"
                  inputName="receivedBy"
                  {...field}
                />
              )}
            />
            {errors.receivedBy?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.receivedBy.message }}
                </StyledMessage>
              </div>
            )}
          </div>
        </div>

        {/* Receiving Notes */}
        <div>
          <Controller
            name="receivingNotes"
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                admin={true}
                text="Receiving Notes"
                inputName="receivingNotes"
                rows={3}
                {...field}
              />
            )}
          />
        </div>

        {/* Line Items */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {props.isEditing ? "Items Received" : "Items to Receive"}
            </h3>
            <BaseButtonSecondary
              type="button"
              onClick={handleReceiveAllRemainingItems}
              className="text-sm"
            >
              {getReceiveAllButtonText()}
            </BaseButtonSecondary>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Ordered
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Remaining
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Received
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Rejected
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Rejection Reason
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {watchedLineItems.map((item, index) => {
                  const isOverReceiving = isReceivingMoreThanOrdered(
                    item,
                    item.quantityReceived || 0,
                  );
                  const excessQty = getExcessQuantity(
                    item,
                    item.quantityReceived || 0,
                  );

                  return (
                    <tr
                      key={item.purchaseOrderItemID}
                      className={isOverReceiving ? "bg-red-50" : ""}
                    >
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {item.title}
                          </div>
                          {item.description && (
                            <div className="text-sm text-gray-500">
                              {item.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-400">
                            {DollarCurrency.format(item.unitPrice)} each
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {item.quantityOrdered}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-blue-600">
                        {calculateRemainingQuantity(item)}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <Controller
                            name={`lineItems.${index}.quantityReceived`}
                            control={control}
                            render={({ field }) => (
                              <BaseInputNumber
                                admin={true}
                                inputName={`quantityReceived-${index}`}
                                min={0}
                                className={`w-20 ${isOverReceiving ? "border-red-300 focus:border-red-500 focus:ring-red-500" : ""}`}
                                value={field.value || ""}
                                onChange={(e) =>
                                  handleNumberInputChange(field, e.target.value)
                                }
                              />
                            )}
                          />
                          {errors.lineItems?.[index]?.quantityReceived
                            ?.message && (
                            <div className="mt-1 text-xs text-red-600">
                              {
                                errors.lineItems[index]?.quantityReceived
                                  ?.message
                              }
                            </div>
                          )}
                          {isOverReceiving && (
                            <div className="mt-1 text-xs text-red-600">
                              ⚠️ Receiving {excessQty} more than ordered
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <Controller
                            name={`lineItems.${index}.quantityRejected`}
                            control={control}
                            render={({ field }) => (
                              <BaseInputNumber
                                admin={true}
                                inputName={`quantityRejected-${index}`}
                                min={0}
                                className="w-20"
                                value={field.value || ""}
                                onChange={(e) =>
                                  handleNumberInputChange(field, e.target.value)
                                }
                              />
                            )}
                          />
                          {errors.lineItems?.[index]?.quantityRejected
                            ?.message && (
                            <div className="mt-1 text-xs text-red-600">
                              {
                                errors.lineItems[index]?.quantityRejected
                                  ?.message
                              }
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Controller
                          name={`lineItems.${index}.rejectionReason`}
                          control={control}
                          render={({ field }) => (
                            <BaseInputText
                              admin={true}
                              inputName={`rejectionReason-${index}`}
                              text="Rejection Reason"
                              className="w-full"
                              {...field}
                            />
                          )}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Over-receiving warning */}
          {hasOverReceivedItems() && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Warning: Over-receiving Items
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      You are attempting to receive more items than were
                      originally ordered. This may indicate a data entry error
                      or vendor over-shipment. Please verify the quantities
                      before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="mt-4 rounded-md bg-gray-50 p-4">
            <div className="text-sm">
              <strong>
                Total Items Received: {calculateTotalQuantityReceived()}
              </strong>
            </div>
          </div>
        </div>

        {/* Validation errors warning */}
        {hasValidationErrors() && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Please Fix Validation Errors
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    Please correct the highlighted errors before submitting the
                    receipt. Check for negative quantities or other invalid
                    values.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex w-full items-center justify-between gap-4">
          <BaseButtonSecondary
            type="button"
            className="w-full justify-center uppercase"
            onClick={() => props.closeDialog()}
          >
            Cancel
          </BaseButtonSecondary>

          <BaseButtonPrimary
            disabled={isSubmitting || props.loading}
            isBusy={isSubmitting || props.loading}
            busyText={props.busyText}
            type="submit"
            className="w-full justify-center uppercase"
          >
            {props.submitButtonText}
          </BaseButtonPrimary>
        </div>
      </form>
    </BaseModal>
  );
}
